import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import config from "../../config/config";
import { Navigate } from "react-router-dom";
import SessionTimeoutDialog from "./SessionTimeoutDialog";
import { Box, CircularProgress } from "@mui/material";
import { startCallerIdBackground, stopCallerIdBackground, isNativePlatform } from "../../utils/callerIdIntegration";

const AuthContext = createContext();

// Sabitler
const TOKEN_REFRESH_INTERVAL = 50 * 60 * 1000;  // 50 dakika (token süresinin %83'ü - 60 dakika token için)
const REFRESH_WARNING_TIME = 20 * 60 * 1000;        // 30 saniye (test için kısa)
const ACTIVITY_THRESHOLD = 20 * 60 * 1000;          // 30 saniye (test için kısa)
const LOGOUT_DELAY = 60 * 1000;                 // 5 saniye (test için kısa)
const MAX_REFRESH_RETRIES = 3;
const RETRY_DELAY = 2000;
const DEBUG = false;  // Debug modunu açık tut

// Debug için logger
const logger = {
  log: (...args) => DEBUG && console.log('[Auth]:', ...args),
  error: (...args) => DEBUG && console.error('[Auth Error]:', ...args),
  warn: (...args) => DEBUG && console.warn('[Auth Warning]:', ...args),
  info: (...args) => DEBUG && console.info('[Auth Info]:', ...args),
};

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => localStorage.getItem('auth_token'));
  const [userData, setUserData] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [lastRefreshTime, setLastRefreshTime] = useState(0);
  const [refreshRetryCount, setRefreshRetryCount] = useState(0);
  const [sessionWarningShown, setSessionWarningShown] = useState(false);
  const [isTimeoutDialogOpen, setIsTimeoutDialogOpen] = useState(false);

  // Referanslar
  const refreshTimeoutRef = useRef(null);
  const activityTimeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const logoutTimerRef = useRef(null);
  const isRefreshingRef = useRef(false);
  const isInitialMount = useRef(true);
  const shouldLogoutRef = useRef(true);
  const tabIdRef = useRef(`tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);

  // Token'dan kullanıcı verilerini çözme
  const decodeUserData = useCallback((tokenToDecode) => {
    if (!tokenToDecode) {
      logger.warn('Token boş, decode edilemedi');
      return null;
    }

    try {
      const base64Url = tokenToDecode.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map(c => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      );
      const decodedToken = JSON.parse(jsonPayload);
      logger.info('Token başarıyla decode edildi');
      // console.log(decodedToken)
      return {
        id: decodedToken.id,
        username: decodedToken.KullaniciAdi,
        fullname: `${decodedToken.Adi || ''} ${decodedToken.Soyadi || ''}`.trim(),
        company: decodedToken.BayiAdi,
        BayiKodu: decodedToken.BayiId,
        Tel: decodedToken.Tel ?? null,
        exp: decodedToken.exp
      };
    } catch (error) {
      logger.error("Token decode hatası:", error);
      return null;
    }
  }, []);

  // Tüm zamanlayıcıları temizleme
  const clearTimers = useCallback(() => {
    logger.log('Zamanlayıcılar temizleniyor');
    [refreshTimeoutRef, activityTimeoutRef, warningTimeoutRef, logoutTimerRef].forEach(ref => {
      if (ref.current) {
        clearTimeout(ref.current);
        ref.current = null;
      }
    });
  }, []);

  // Eski aktivite kayıtlarını temizle
  const cleanupOldActivity = useCallback(() => {
    const now = Date.now();
    const keys = Object.keys(localStorage);
    let removedCount = 0;

    // Sadece mevcut sekmenin aktivitesini tut, diğer tüm aktivite kayıtlarını temizle
    for (const key of keys) {
      if (key.startsWith('last_activity_') || key.startsWith('tab_activity_')) {
        if (!key.includes(tabIdRef.current)) {
          localStorage.removeItem(key);
          removedCount++;
        }
      }
    }

    if (removedCount > 0) {
      logger.log(`${removedCount} eski aktivite kaydı temizlendi`);
    }

    // Şimdiki sekmenin aktivitesini güncelle
    const currentTime = now.toString();
    localStorage.setItem(`tab_activity_${tabIdRef.current}`, currentTime);
    localStorage.setItem(`last_activity_${tabIdRef.current}`, currentTime);

    logger.log(`Sekme aktivitesi sıfırlandı: ${tabIdRef.current}, zaman: ${new Date(parseInt(currentTime)).toLocaleTimeString()}`);
  }, []);

  // Direkt çıkış yapma işlemi - zamanlayıcılar için
  const directLogout = useCallback((reason = 'TIMEOUT') => {
    // console.log(`%c 🔒 DIREKT LOGOUT - ${reason}`,
    //            'background: red; color: white; font-size: 16px; padding: 5px;');

    // Temizlik
    clearTimers();
    localStorage.removeItem(`tab_activity_${tabIdRef.current}`);
    localStorage.removeItem(`last_activity_${tabIdRef.current}`);
    localStorage.setItem('logout_initiator', tabIdRef.current);
    localStorage.removeItem('auth_token');
    stopCallerIdBackground();

    // State'i temizle
    setToken(null);
    setUserData(null);
    setIsAuthenticated(false);
    setLastRefreshTime(0);
    setRefreshRetryCount(0);
    setSessionWarningShown(false);

    // Sayfayı direkt yönlendir
    window.location.href = '/login';
  }, [clearTimers]);

  // Normal logout işlemi - kullanıcı etkileşimleri için
  const logout = useCallback(async (reason = '') => {
    logger.log('Çıkış yapılıyor, sebep:', reason);
    clearTimers();

    try {
      if (token) {
        logger.log(`Logout API isteği gönderiliyor: ${config.baseURL}/logout`);
        const response = await fetch(`${config.baseURL}/logout`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          },
          credentials: 'include',
          body: JSON.stringify({ token })
        });

        if (!response.ok) {
          logger.warn(`Logout isteği başarısız oldu: ${response.status} ${response.statusText}`);
          logger.warn('Logout API başarısız oldu, ancak local temizlik devam edecek');
        } else {
          logger.log('Logout API başarılı');
        }
      }
    } catch (error) {
      logger.error("Logout API hatası:", error);
    } finally {
      logger.log('Logout yerel temizlik işlemleri başlatılıyor');

      // Bu sekmeyi aktif olmayan olarak işaretle
      localStorage.removeItem(`tab_activity_${tabIdRef.current}`);

      // Çıkış olayını diğer sekmelere bildirmek için 'auth_token' değerini localStorage'dan sil
      // Ancak diğer sekmelerin gereksiz çıkış yapmasını önlemek için bir bayrak ayarla
      localStorage.setItem('logout_initiator', tabIdRef.current);

      // Lokal state'i temizle
      localStorage.removeItem('auth_token');
      stopCallerIdBackground();
      setToken(null);
      setUserData(null);
      setIsAuthenticated(false);
      setLastRefreshTime(0);
      setRefreshRetryCount(0);
      setSessionWarningShown(false);

      if (reason === 'MULTIPLE_SESSIONS') {
        alert('Başka bir cihazda oturum açıldı. Güvenlik nedeniyle çıkış yapıldı.');
      }

      // Debug için logout nedenini konsola yazdır
      logger.log(`Logout tamamlandı, neden: ${reason}`);

      // Bayrak değerini temizle ve yönlendirme yap
      setTimeout(() => {
        localStorage.removeItem('logout_initiator');
        logger.log('Login sayfasına yönlendiriliyor');
        window.location.href = '/login';
      }, 100);
    }
  }, [token, clearTimers]);

  // Token yenileme
  const refreshToken = useCallback(async (force = false) => {
    if (!token) {
      logger.warn('Token yenileme atlandı: token yok');
      return false;
    }

    if (!force && !isAuthenticated) {
      logger.warn('Token yenileme atlandı: oturum açık değil');
      return false;
    }

    if (isRefreshingRef.current) {
      logger.warn('Token zaten yenileniyor');
      return false;
    }

    try {
      isRefreshingRef.current = true;
      logger.info(`Token yenileme denemesi (${refreshRetryCount + 1}/${MAX_REFRESH_RETRIES})`);

      const response = await fetch(`${config.baseURL}/refresh-token`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        credentials: 'include',
        body: JSON.stringify({ token })
      });

      if (response.status === 401 || response.status === 403) {
        logger.error('Token yenileme yetkisiz');
        await logout('UNAUTHORIZED');
        return false;
      }

      if (!response.ok) {
        throw new Error(`HTTP Error: ${response.status}`);
      }

      const data = await response.json();
      if (!data.token) {
        throw new Error('Yeni token alınamadı');
      }

      logger.log('Yeni token başarıyla alındı');

      // Sessizce localStorage'ı güncelle (event tetiklemeden)
      localStorage.setItem('token_update_silent', 'true');
      localStorage.setItem('auth_token', data.token);
      setTimeout(() => localStorage.removeItem('token_update_silent'), 100);

      setToken(data.token);
      setLastRefreshTime(Date.now());
      setRefreshRetryCount(0);

      const newUserData = decodeUserData(data.token);
      if (newUserData) {
        setUserData(newUserData);
        setIsAuthenticated(true);
      }

      return true;
    } catch (error) {
      logger.error("Token yenileme hatası:", error);

      if (refreshRetryCount < MAX_REFRESH_RETRIES - 1) {
        setRefreshRetryCount(prev => prev + 1);
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
        return refreshToken(force);
      }

      await logout('TOKEN_REFRESH_FAILED');
      return false;
    } finally {
      isRefreshingRef.current = false;
    }
  }, [token, isAuthenticated, refreshRetryCount, decodeUserData, logout]);

  // Sekme aktivitesini kaydet - artık sadece mevcut sekme için
  const updateTabActivity = useCallback(() => {
    if (isAuthenticated && token) {
      const now = Date.now().toString();
      const tabActivityKey = `tab_activity_${tabIdRef.current}`;
      const lastActivityKey = `last_activity_${tabIdRef.current}`;

      localStorage.setItem(tabActivityKey, now);
      localStorage.setItem(lastActivityKey, now);

      logger.log(`Sekme aktivitesi güncellendi: ${tabIdRef.current}, zaman: ${new Date(parseInt(now)).toLocaleTimeString()}`);
    }
  }, [isAuthenticated, token]);

  // Mobil: Giriş yapıldığında Caller ID arka plan servisini başlat (lisans arayüzü + arka planda arama kaydı)
  useEffect(() => {
    if (!isAuthenticated || !userData || !isNativePlatform()) return;
    const baseUrl = `${config.baseURL.replace(/\/$/, "")}/caller-id`;
    const apiKey = typeof process !== "undefined" && process.env?.REACT_APP_CALLER_ID_API_KEY ? process.env.REACT_APP_CALLER_ID_API_KEY : "";
    const devicePhone = userData.Tel || "";
    if (typeof console !== "undefined" && console.log) {
      console.log("[Caller ID] Başlatılıyor:", { baseUrl, hasApiKey: !!apiKey, devicePhone: devicePhone || "(boş — Kullanici.Tel dolu olmalı)" });
    }
    startCallerIdBackground({ baseUrl, apiKey, devicePhone });
  }, [isAuthenticated, userData]);

  // Herhangi bir sekme aktif mi kontrol et - sadece bu sekmeyi dikkate al
  // isAnyTabActive fonksiyonunu şöyle değiştirin:
const isAnyTabActive = useCallback(() => {
  const now = Date.now();
  const keys = Object.keys(localStorage);
  let anyTabActive = false;

  // TÜM sekmelerin aktivitesini kontrol et
  for (const key of keys) {
    if (key.startsWith('last_activity_')) {
      const lastActivity = parseInt(localStorage.getItem(key) || '0');
      const timeSinceActivity = now - lastActivity;

      if (timeSinceActivity < ACTIVITY_THRESHOLD) {
        logger.log(`Aktif sekme bulundu: ${key}, son aktivite: ${Math.round(timeSinceActivity/1000)} saniye önce`);
        anyTabActive = true;
        break;
      }
    }
  }

  if (anyTabActive) {
    logger.log('En az bir sekme aktif');
  } else {
    logger.log('Hiçbir sekme aktif değil');
  }

  return anyTabActive;
}, []);
  // Zamanlı token yenileme
  useEffect(() => {
    if (isAuthenticated && token) {
      const startTokenRefresh = () => {
        if (refreshTimeoutRef.current) {
          clearTimeout(refreshTimeoutRef.current);
        }

        // Token sona erme süresine göre yenileme zamanını hesapla
        let refreshInterval = TOKEN_REFRESH_INTERVAL;
        if (userData && userData.exp) {
          const expiryTime = userData.exp * 1000; // Unix timestamp to milliseconds
          const currentTime = Date.now();
          const timeToExpiry = expiryTime - currentTime;

          // Sona ermeden 5 dakika önce yenile (minimum 5 saniye, maksimum TOKEN_REFRESH_INTERVAL)
          const safePeriod = 5 * 60 * 1000;
          refreshInterval = Math.max(5000, Math.min(timeToExpiry - safePeriod, TOKEN_REFRESH_INTERVAL));

          logger.log(`Token son kullanma süresi: ${new Date(expiryTime).toLocaleString()}`);
          logger.log(`Hesaplanan yenileme aralığı: ${refreshInterval/1000} saniye`);
        }

        logger.log(`Token yenileme zamanlayıcısı başlatılıyor: ${refreshInterval/1000} saniye`);
        refreshTimeoutRef.current = setTimeout(() => {
          logger.log('Zamanlanmış token yenileme başlatılıyor');
          refreshToken().then(success => {
            if (success) {
              logger.log('Token başarıyla yenilendi, yeni zamanlayıcı başlatılıyor');
              startTokenRefresh();
            } else {
              logger.error('Token yenileme başarısız');
            }
          });
        }, refreshInterval);
      };

      startTokenRefresh();
      return () => {
        if (refreshTimeoutRef.current) {
          clearTimeout(refreshTimeoutRef.current);
        }
      };
    }
  }, [isAuthenticated, token, userData, refreshToken]);

  // Kullanıcı aktivitesi izleme ve oturum zaman aşımı kontrolü
  useEffect(() => {
    if (isAuthenticated) {
      // İlk çalıştırma anında diğer sekmelerin aktivite kayıtlarını temizle
      cleanupOldActivity();

      const resetActivityTimers = () => {
        if (sessionWarningShown) {
          logger.log('Uyarı gösterildiği için zamanlayıcılar sıfırlanmadı');
          return;
        }

        // Aktivite zamanlayıcılarını temizle
        if (warningTimeoutRef.current) {
          clearTimeout(warningTimeoutRef.current);
        }

        if (logoutTimerRef.current) {
          clearTimeout(logoutTimerRef.current);
        }

        // Bu sekmenin aktivite durumunu güncelle
        updateTabActivity();

        // Uyarı zamanlayıcısını ayarla
        warningTimeoutRef.current = setTimeout(() => {
          logger.log('Aktivite kontrolü yapılıyor...');
          // Aktif sekme var mı diye kontrol et
          if (!isAnyTabActive()) {
            logger.log('Bu sekme artık aktif değil, oturum uyarısı gösteriliyor');

            // Önce state'i güncelle
            setSessionWarningShown(true);
            setIsTimeoutDialogOpen(true);
            shouldLogoutRef.current = true;

            // console.log('%c ⚠️ OTURUM ZAMANAŞIMI UYARISI GÖSTERILDI! 5 saniye içinde otomatik çıkış yapılacak!',
            //           'background: #dd0000; color: white; font-size: 16px; padding: 5px;');

            // Uyarıdan sonra logout zamanlayıcısını başlat
            clearTimeout(logoutTimerRef.current); // Önceki zamanlayıcıyı temizle

            // console.log('%c ⏱️ LOGOUT ZAMANLAYICISI BAŞLATILDI - ' + LOGOUT_DELAY/1000 + ' saniye',
            //           'background: orange; color: black; font-size: 16px; padding: 5px;');

            // Basitleştirilmiş logout işlemi - async/await kullanmadan
            logoutTimerRef.current = setTimeout(() => {
              directLogout('INACTIVITY');
            }, LOGOUT_DELAY);
          } else {
            logger.log('Bu sekme hala aktif, zamanlayıcılar sıfırlanıyor');
            resetActivityTimers();
          }
        }, REFRESH_WARNING_TIME);
      };

      // Kullanıcı aktivitesini izleyen olaylar
      const events = [
        'mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart',
        'click', 'keypress', 'input', 'change', 'focus'
      ];

      events.forEach(event => document.addEventListener(event, resetActivityTimers));

      // İlk çağrı
      resetActivityTimers();

      // Düzenli aktivite kontrolü
      const checkIntervalRef = setInterval(() => {
        logger.log('Düzenli aktivite kontrolü...');
        const isWarningDisplayed = sessionWarningShown;

        if (!isWarningDisplayed) {
          if (!isAnyTabActive()) {
            logger.log('Düzenli kontrol: Bu sekme artık aktif değil, zamanlayıcıyı tetikle');
            if (warningTimeoutRef.current) {
              clearTimeout(warningTimeoutRef.current);
            }

            // Hemen uyarı göster
            setSessionWarningShown(true);
            setIsTimeoutDialogOpen(true);
            shouldLogoutRef.current = true;

            // console.log('%c ⚠️ DÜZENLİ KONTROL - OTURUM ZAMANAŞIMI UYARISI GÖSTERILDI! 5 saniye içinde otomatik çıkış yapılacak!',
            //           'background: #dd0000; color: white; font-size: 16px; padding: 5px;');

            // Uyarıdan sonra logout zamanlayıcısını başlat
            clearTimeout(logoutTimerRef.current);

            // console.log('%c ⏱️ DÜZENLİ KONTROL - LOGOUT ZAMANLAYICISI BAŞLATILDI - ' + LOGOUT_DELAY/1000 + ' saniye',
            //           'background: orange; color: black; font-size: 16px; padding: 5px;');

            logoutTimerRef.current = setTimeout(() => {
              directLogout('INACTIVITY_PERIODIC');
            }, LOGOUT_DELAY);
          } else {
            logger.log('Düzenli kontrol: Bu sekme hala aktif');
          }
        }
      }, 30000); // 30 saniyede bir kontrol et

      return () => {
        events.forEach(event => document.removeEventListener(event, resetActivityTimers));
        clearInterval(checkIntervalRef);
        if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
        if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
      };
    }
  }, [isAuthenticated, sessionWarningShown, logout, updateTabActivity, isAnyTabActive, cleanupOldActivity, directLogout]);

  // Basitleştirilmiş sekmeler arası senkronizasyon - sadece logout işlemi için
  useEffect(() => {
    const handleStorageChange = (e) => {
      // Sadece auth_token değişikliklerini izle
      if (e.key === 'auth_token') {
        // Token silindiyse ve bu sekme çıkışı başlatmadıysa
        if (!e.newValue && token) {
          const initiator = localStorage.getItem('logout_initiator');
          if (initiator && initiator === tabIdRef.current) {
            logger.log('Bu sekme logout işlemini başlattı, tekrar logout yapmaya gerek yok');
            return;
          }

          logger.log('Başka sekme oturumu kapattı, bu sekme de çıkış yapıyor');
          localStorage.removeItem(`last_activity_${tabIdRef.current}`);
          localStorage.removeItem(`tab_activity_${tabIdRef.current}`);
          directLogout('OTHER_TAB_LOGOUT');
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [token, directLogout]);

  // İlk yükleme ve oturum kontrolü - basitleştirilmiş
  useEffect(() => {
    if (!isInitialMount.current) return;

    const checkSession = async () => {
      try {
        // Başlangıçta tüm eski aktivite kayıtlarını temizle
        cleanupOldActivity();

        const storedToken = localStorage.getItem('auth_token');
        if (!storedToken) {
          setIsAuthenticated(false);
          setIsLoading(false);
          return;
        }

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 12_000);

        const response = await fetch(`${config.baseURL}/check-session`, {
          headers: {
            Authorization: `Bearer ${storedToken}`,
            "Content-Type": "application/json"
          },
          credentials: 'include',
          signal: controller.signal,
        }).finally(() => clearTimeout(timeoutId));

        const data = await response.json();

        if (!response.ok || !data.success) {
          if (data.reason === 'MULTIPLE_SESSIONS') {
            await logout('MULTIPLE_SESSIONS');
          } else {
            await logout();
          }
          return;
        }

        if (data.needsRefresh) {
          const refreshSuccess = await refreshToken(true);
          if (!refreshSuccess) {
            throw new Error('Token refresh failed');
          }
          setIsLoading(false);
          return;
        }

        if (!data.user) {
          throw new Error('No user data received');
        }

        const formattedUserData = {
          id:data.user.id,
          username: data.user.name,
          fullname: `${data.user.Adi} ${data.user.Soyadi}`,
          company: data.user.BayiAdi,
          BayiKodu: data.user.BayiId,
        };

        setUserData(formattedUserData);
        setToken(storedToken);
        setIsAuthenticated(true);
        setLastRefreshTime(Date.now());

        // Bu sekmeyi aktif olarak işaretle
        updateTabActivity();

      } catch (error) {
        logger.error("Session check error:", error);
        localStorage.removeItem('auth_token');
        setToken(null);
        setUserData(null);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
        isInitialMount.current = false;
      }
    };

    checkSession();

    // Sayfa kapanırken temizlik işlemleri
    const handleBeforeUnload = () => {
      localStorage.removeItem(`tab_activity_${tabIdRef.current}`);
      localStorage.removeItem(`last_activity_${tabIdRef.current}`);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      clearTimers();
    };
  }, [logout, clearTimers, refreshToken, updateTabActivity, cleanupOldActivity]);

  // Yetki kontrolü
  const hasPermission = (requiredRole) => {
    const adminCodes = [1, 0];
    return adminCodes.includes(userData?.BayiKodu);
  };

  const contextValue = {
    token,
    userData,
    isAuthenticated,
    isLoading,
    login: useCallback(async (newToken) => {
      try {
        logger.log('Login işlemi başlatılıyor');

        if (!newToken) {
          throw new Error("Token gerekli");
        }

        const newUserData = decodeUserData(newToken);
        if (!newUserData) {
          throw new Error("Geçersiz token");
        }

        clearTimers();
        setRefreshRetryCount(0);
        setSessionWarningShown(false);

        logger.log('Login bilgileri kaydediliyor');
        localStorage.setItem('auth_token', newToken);
        setToken(newToken);
        setUserData(newUserData);
        setIsAuthenticated(true);
        setLastRefreshTime(Date.now());

        // Bu sekmeyi aktif olarak işaretle
        updateTabActivity();

        // İlk girişte token yenilemeyi beklet (özellikle mobilde bağlantı sorunlarını maskelememek için)
        return true;
      } catch (error) {
        logger.error("Login hatası:", error);
        throw error;
      }
    }, [decodeUserData, clearTimers, refreshToken, updateTabActivity]),
    logout,
    refreshToken,
    hasPermission,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      <SessionTimeoutDialog
        open={isTimeoutDialogOpen}
        onContinue={() => {
          logger.log('Kullanıcı oturuma devam etmeyi seçti');
          shouldLogoutRef.current = false;
          setIsTimeoutDialogOpen(false);
          setSessionWarningShown(false);

          // Zamanlayıcıları temizle
          if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
          if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);

          // Aktivite durumunu güncelle
          updateTabActivity();

          // Token'ı yenile
          refreshToken(true);
        }}
        onLogout={() => {
          logger.log('Kullanıcı manuel logout seçti');
          shouldLogoutRef.current = true; // Logout isteğini garanti et
          setIsTimeoutDialogOpen(false);
          logout('MANUAL_LOGOUT');
        }}
      />
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// AdminRoute.tsx
export const AdminRoute = ({ children }) => {
  const { isAuthenticated, token, isLoading, userData } = useAuth();

  if (isLoading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  const isAdmin = userData?.BayiKodu === 1 || userData?.BayiKodu === 0; // Ana bayi kodlarını kontrol et
  const isValidSession = isAuthenticated && token && isAdmin;

  return isValidSession ? children : <Navigate to="/404" />;
};

export const PrivateRoute = ({ children }) => {
  const { isAuthenticated, token, isLoading } = useAuth();

  if (isLoading) {
    return (
      <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      className="fixed inset-0 bg-white"
    >
      <CircularProgress />
    </Box>
    );
  }

  const isValidSession = isAuthenticated && token;
  return isValidSession ? children : <Navigate to="/login" />;
};